import React, { useState } from "react";
import { useSearchParams, useNavigate, Link } from "react-router-dom";
import Header from "@/components/layout/Header";
import api from "@/lib/api";
import { toast } from "sonner";
import { useAuth } from "@/contexts/AuthContext";
import { Search, Shield, Building2, CheckCircle2, Send } from "lucide-react";

const ROLES = [
  ["owner", "Organization Owner"], ["director", "Director"], ["general_manager", "General Manager"],
  ["head_coach", "Head Coach"], ["assistant_coach", "Assistant Coach"], ["team_manager", "Team Manager"], ["other", "Other"],
];
const inp = "w-full border border-slate-200 rounded-sm px-3 py-2.5 text-sm";

export default function ClaimOwnership() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [params] = useSearchParams();
  const [q, setQ] = useState("");
  const [results, setResults] = useState(null);
  const preId = params.get("id"), preType = params.get("type"), preName = params.get("name");
  const [target, setTarget] = useState(preId ? { id: preId, target_type: preType, name: preName } : null);
  const [f, setF] = useState({ requester_name: user?.name || "", requester_email: user?.email || "", requester_phone: "", requester_role: "director", message: "" });
  const [busy, setBusy] = useState(false);

  const search = () => {
    if (q.trim().length < 2) return toast.error("Type at least 2 characters");
    api.get(`/live/claimable/search`, { params: { q } }).then(({ data }) =>
      setResults([...(data.organizations || []), ...(data.teams || [])])).catch(() => setResults([]));
  };

  const submit = () => {
    if (!user) { toast.error("Please sign in to submit a claim"); return navigate("/login"); }
    if (!f.requester_name || !f.requester_email || !f.message) return toast.error("Name, email and message are required");
    setBusy(true);
    api.post("/live/claims", { target_type: target.target_type, target_id: target.id, ...f })
      .then(() => { toast.success("Claim submitted — CBR will review and be in touch."); navigate("/live"); })
      .catch((e) => toast.error(e?.response?.data?.detail || "Failed to submit claim")).finally(() => setBusy(false));
  };

  return (
    <div className="min-h-screen bg-[#F1F3F6]"><Header />
      <div className="bg-[#0A192F] text-white">
        <div className="max-w-3xl mx-auto px-6 py-10">
          <div className="label-overline text-[#E63946]">CBR Ownership</div>
          <h1 className="font-display text-4xl uppercase tracking-tighter mt-1">Claim Your Organization or Team</h1>
          <p className="text-sm text-white/60 mt-2 max-w-xl">Already listed on CBR? Request ownership to manage your rosters, schedules, scoring and rankings. CBR will review your request.</p>
        </div>
      </div>

      <div className="max-w-3xl mx-auto px-6 py-8" data-testid="claim-ownership-page">
        {!target ? (
          <div className="bg-white border border-slate-200 rounded-sm p-6">
            <div className="label-overline mb-2">Find your organization or team</div>
            <div className="flex gap-2">
              <input data-testid="claim-search-input" value={q} onChange={(e) => setQ(e.target.value)} onKeyDown={(e) => e.key === "Enter" && search()} placeholder="e.g. Ontario Blue Jays" className={inp} />
              <button data-testid="claim-search-btn" onClick={search} className="bg-[#0A192F] text-white px-4 rounded-sm flex items-center gap-2 text-sm font-semibold uppercase tracking-wider"><Search className="w-4 h-4" /> Search</button>
            </div>
            {results && (
              <div className="mt-4 space-y-2" data-testid="claim-search-results">
                {results.length === 0 ? <div className="text-sm text-slate-400 py-6 text-center">No matches. Not listed yet? <Link to="/live" className="text-[#E63946] font-semibold">Contact CBR</Link>.</div>
                  : results.map((r) => (
                    <button key={`${r.target_type}-${r.id}`} data-testid={`claim-result-${r.id}`} onClick={() => setTarget(r)}
                      className="w-full flex items-center gap-3 p-3 border border-slate-100 rounded-sm hover:border-[#E63946] text-left">
                      {r.target_type === "organization" ? <Building2 className="w-5 h-5 text-slate-400" /> : <Shield className="w-5 h-5 text-slate-400" />}
                      <div className="flex-1 min-w-0"><div className="text-sm font-semibold truncate">{r.name}</div>
                        <div className="text-[11px] text-slate-400 capitalize">{r.target_type} · {(r.management_status || "").replace("_", " ")}{r.province ? ` · ${r.province}` : ""}</div></div>
                      {r.verified ? <span className="text-[10px] font-bold text-emerald-600 flex items-center gap-1"><CheckCircle2 className="w-3.5 h-3.5" /> Verified</span>
                        : r.has_owner ? <span className="text-[10px] text-slate-400">Managed</span> : <span className="text-[10px] font-bold text-[#E63946]">Claimable</span>}
                    </button>
                  ))}
              </div>
            )}
          </div>
        ) : (
          <div className="bg-white border border-slate-200 rounded-sm p-6" data-testid="claim-request-form">
            <div className="flex items-center justify-between mb-4">
              <div><div className="label-overline text-[#E63946]">Claiming</div>
                <div className="font-display text-2xl uppercase tracking-tight">{target.name}</div>
                <div className="text-[11px] text-slate-400 capitalize">{target.target_type}</div></div>
              {!preId && <button onClick={() => setTarget(null)} className="text-xs font-semibold text-slate-500 hover:text-[#E63946]">Change</button>}
            </div>
            <div className="grid sm:grid-cols-2 gap-3">
              <input data-testid="claim-name" placeholder="Your name" value={f.requester_name} onChange={(e) => setF({ ...f, requester_name: e.target.value })} className={inp} />
              <input data-testid="claim-email" placeholder="Email address" value={f.requester_email} onChange={(e) => setF({ ...f, requester_email: e.target.value })} className={inp} />
              <input data-testid="claim-phone" placeholder="Phone (optional)" value={f.requester_phone} onChange={(e) => setF({ ...f, requester_phone: e.target.value })} className={inp} />
              <select data-testid="claim-role" value={f.requester_role} onChange={(e) => setF({ ...f, requester_role: e.target.value })} className={inp}>
                {ROLES.map(([v, l]) => <option key={v} value={v}>{l}</option>)}
              </select>
            </div>
            <textarea data-testid="claim-message" rows={3} placeholder="Tell us about your role (e.g. I'm the Director of the Ontario Nationals)." value={f.message} onChange={(e) => setF({ ...f, message: e.target.value })} className={`${inp} mt-3`} />
            <button data-testid="claim-submit-btn" disabled={busy} onClick={submit} className="mt-4 w-full sm:w-auto flex items-center justify-center gap-2 bg-[#E63946] text-white text-sm font-semibold uppercase tracking-wider px-6 py-3 rounded-sm hover:bg-[#c62f3b] disabled:opacity-50"><Send className="w-4 h-4" /> Submit Claim Request</button>
          </div>
        )}
      </div>
    </div>
  );
}
